import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Button, Alert, Collapse } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaArrowLeft, FaSave, FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { selectAuthUserId } from '../redux/authSlice';
import { setPreviewLesson } from '../redux/classroomSlice';
import LibrarianVideoAssistant from '../components/video/LibrarianVideoAssistant';
import CourseDetailsModal from '../components/ui/CourseDetailsModal';
import {
  fetchVideoForWatching,
  createWatchSession,
  updateWatchSession,
  updateVideoTimestamp,
} from '../services/supabaseClient/videoLibraryService';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import './VideoWatchPage.css';

// Helper function to format seconds to MM:SS or HH:MM:SS
const formatTime = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const VideoWatchPage = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector(selectAuthUserId);

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [isSaved, setIsSaved] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const playerRef = useRef(null);
  const progressSaveInterval = useRef(null);

  useEffect(() => {
    if (userId && videoId) {
      loadVideo();
    }

    return () => {
      // Cleanup: save progress when leaving page
      if (progressSaveInterval.current) {
        clearInterval(progressSaveInterval.current);
      }
      if (sessionId && currentTime > 0) {
        endWatchSession();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, videoId]);

  const loadVideo = async () => {
    setLoading(true);
    setError(null);

    try {
      // Add minimum delay for smooth transition
      const [{ data, error: fetchError }] = await Promise.all([
        fetchVideoForWatching(videoId, userId),
        new Promise((resolve) => setTimeout(resolve, 500)), // 500ms minimum delay
      ]);

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Video not found');

      setVideo(data);
      setIsSaved(true);

      // Create watch session
      const { data: session } = await createWatchSession(userId, videoId, data.course_id);
      if (session) {
        setSessionId(session.id);
      }

      // Start auto-save interval for progress
      progressSaveInterval.current = setInterval(() => {
        if (currentTime > 0) {
          saveProgress();
        }
      }, 30000); // Save every 30 seconds
    } catch (err) {
      setError(err.message || 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!video || currentTime === 0) return;

    try {
      await updateVideoTimestamp(video.id, Math.floor(currentTime));
      setIsSaved(true);
      // Reset saved indicator after 2 seconds
      setTimeout(() => setIsSaved(false), 2000);
    } catch (_err) {
      // no-op
    }
  };

  const handleManualSave = async () => {
    await saveProgress();
  };

  const endWatchSession = async () => {
    if (!sessionId) return;

    try {
      const completionPercentage = video?.duration
        ? Math.round((currentTime / video.duration) * 100)
        : 0;

      await updateWatchSession(sessionId, {
        ended_at: new Date().toISOString(),
        duration_watched: Math.floor(currentTime),
        completion_percentage: completionPercentage,
      });
    } catch (_err) {
      // no-op
    }
  };

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleSeekTo = (time) => {
    // This would be used to seek the video player
    // Implementation depends on how we integrate with YouTube iframe API
    setCurrentTime(time);
  };

  const handleBack = () => {
    navigate('/library');
  };

  const handleJumpToLesson = (index) => {
    const lesson = video?.ml_courses_staging?.units?.[index];
    if (lesson) {
      dispatch(setPreviewLesson(lesson));
    }
  };

  const handleStartCourse = () => {
    if (video?.ml_courses_staging) {
      navigate(`/courses/${video.ml_courses_staging.id}`);
    }
  };

  const handleForkCourse = () => {
    // No-op until fork flow is implemented.
  };

  if (loading) {
    return (
      <Container fluid className="video-watch-page">
        <LoadingSpinner message="Loading video..." />
      </Container>
    );
  }

  if (error || !video) {
    return (
      <Container fluid className="video-watch-page">
        <Alert variant="danger" className="m-4">
          {error || 'Video not found'}
        </Alert>
        <Button variant="secondary" onClick={handleBack} className="m-4">
          <FaArrowLeft className="me-2" />
          Back to Library
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="video-watch-page">
      <Row className="video-watch-header">
        <Col>
          <Button
            variant="link"
            onClick={handleBack}
            className="back-button"
            aria-label="Back to Library"
            title="Back to Library"
          >
            <FaArrowLeft />
          </Button>
        </Col>
      </Row>

      <Row className="video-watch-content">
        {/* Video Panel - Left Side */}
        <Col md={7} className="video-panel">
          <Card className="video-player-card">
            <div className="video-player-container">
              <iframe
                ref={playerRef}
                src={`https://www.youtube.com/embed/${video.video_id}?enablejsapi=1&start=${Math.floor(video.playback_position_seconds || 0)}`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
              />
            </div>

            <Card.Body>
              {/* Collapsible Header */}
              <div className="video-details-toggle">
                <div className="d-flex align-items-start gap-3">
                  <Button
                    variant="link"
                    className="text-muted p-0 dropdown-toggle-btn"
                    onClick={() => setShowDetails(!showDetails)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowDetails(!showDetails);
                      }
                    }}
                    aria-label={showDetails ? 'Hide details' : 'Show details'}
                  >
                    {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                  </Button>
                  <div className="flex-grow-1">
                    <h3 className="video-watch-title mb-1">{video.title}</h3>
                    {video.channel_name && (
                      <small className="text-muted">{video.channel_name}</small>
                    )}
                  </div>
                  <Button
                    variant={isSaved ? 'success' : 'outline-primary'}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManualSave();
                    }}
                    disabled={currentTime === 0}
                  >
                    <FaSave className="me-1" />
                    {isSaved ? 'Saved!' : 'Save Progress'}
                  </Button>
                </div>
              </div>

              {/* Collapsible Content */}
              <Collapse in={showDetails}>
                <div>
                  <div className="video-details-content">
                    {/* Resume Time Indicator */}
                    {video.playback_position_seconds > 0 && (
                      <Alert
                        variant="info"
                        className="d-flex justify-content-between align-items-center mb-3"
                      >
                        <div>
                          <strong>Resume from:</strong>{' '}
                          {formatTime(video.playback_position_seconds)}
                          {video.last_watched_at && (
                            <small className="text-muted d-block">
                              Last watched: {new Date(video.last_watched_at).toLocaleDateString()}
                            </small>
                          )}
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSeekTo(video.playback_position_seconds)}
                        >
                          Resume
                        </Button>
                      </Alert>
                    )}

                    {video.ml_courses_staging && (
                      <div className="video-course-section">
                        <h5>Course</h5>
                        <div
                          className="course-badge-plain clickable"
                          onClick={() => setShowCourseModal(true)}
                          role="button"
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowCourseModal(true);
                            }
                          }}
                        >
                          {video.ml_courses_staging.title}
                        </div>
                      </div>
                    )}

                    {video.description && (
                      <div className="video-description-section">
                        <h5>Description</h5>
                        <p className="video-description-text">{video.description}</p>
                      </div>
                    )}

                    {video.tags && video.tags.length > 0 && (
                      <div className="video-tags-section">
                        <h6>Tags</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {video.tags.map((tag, index) => (
                            <Badge key={index} bg="secondary">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {video.notes && (
                      <div className="video-notes-section mt-3">
                        <h6>Your Notes</h6>
                        <p className="text-muted">{video.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </Col>

        {/* Librarian Panel - Right Side */}
        <Col md={5} className="librarian-panel">
          <LibrarianVideoAssistant
            video={video}
            transcript={video.transcript}
            currentTime={currentTime}
            onSeekTo={handleSeekTo}
            onTimeUpdate={handleTimeUpdate}
            sessionId={sessionId}
          />
        </Col>
      </Row>

      {/* Course Details Modal */}
      {video?.ml_courses_staging && (
        <CourseDetailsModal
          show={showCourseModal}
          onHide={() => setShowCourseModal(false)}
          course={video.ml_courses_staging}
          onStart={handleStartCourse}
          onFork={handleForkCourse}
          onJumpToLesson={handleJumpToLesson}
        />
      )}
    </Container>
  );
};

export default VideoWatchPage;
